import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import classNames from 'classnames';
import { authService } from 'auth/services';

import { useIsAuthenticated } from '@azure/msal-react';

import { fetchOrderPreAppointments, updateAppointmentDateAndTime } from 'features/order-tracker/store/actions';
import { getSelectedAppointmentDate } from 'features/order-tracker/store/selectors';
import { formatSelectedWeekdayValue, getMonthName } from 'features/order-tracker/utils';

import { DAYS_OF_WEEK } from './constants';

import styles from './styles.module.scss';

export const DatePicker: React.FC<components.DatePickerProps> = (props) => {
  const {
    monthInfo: { daysForCalendar, firstAvailableDateInMonth, month, year },
    monthOffset,
  } = props;
  const dispatch = useDispatch();
  const isAuthenticated = useIsAuthenticated();
  const selectedDate = useSelector(getSelectedAppointmentDate);
  const [, selectedMonth, selectedDay] = selectedDate?.split('-').map((item) => +item) || [];
  const selectedMonthName = getMonthName(selectedMonth - 1);

  const handleDateSelection = (event: React.MouseEvent<EventTarget>): void => {
    const day = +event.target.innerText;
    const formattedDate = formatSelectedWeekdayValue(year, month, day);

    isAuthenticated && authService.throttledRefreshToken();
    dispatch(fetchOrderPreAppointments({ date: formattedDate }));
    dispatch(
      updateAppointmentDateAndTime({
        selectedDate: formattedDate,
        selectedSlot: '',
      }),
    );
  };

  const renderDatePickerHeader = () => (
    <div className={styles.datePickerHeader}>
      {DAYS_OF_WEEK.map((day) => (
        <h6 key={day} className={styles.dayHeading}>
          {day}
        </h6>
      ))}
    </div>
  );

  const renderDays = () => (
    <div className={styles.datePickerContent}>
      {daysForCalendar.map((day, idx) => {
        const invisible = day <= 0;
        const isWeekend = idx % 7 >= 5;
        const disabled = invisible || isWeekend || (!monthOffset && day < firstAvailableDateInMonth);
        const selected = month === selectedMonthName && day === selectedDay;
        const actionHandler = disabled ? undefined : handleDateSelection;

        return (
          <button
            key={day}
            className={classNames(styles.day, {
              [styles.disabled]: disabled,
              [styles.invisible]: invisible,
              [styles.selected]: selected,
            })}
            onClick={actionHandler}
            type="button"
          >
            {day}
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      {renderDatePickerHeader()}
      {renderDays()}
    </>
  );
};
